import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { convertToSnakeCase } from '@/models/utils/casing'
import { useFilteredJobStoreLegacyAdapter } from '@/components/jobs/job-search-page/filteredJobStore'
import { useJobFilterStore } from '../JobsFilterStore'
import { getInstance } from '@/auth'
import moment from 'moment'

interface Feature {
  id: string
  type: 'Feature'
  geometry: {
    type: 'Point'
    coordinates: [number, number]
  }
  properties: {
    LocationId: number
    LocationName: string
    Latitude: number
    Longitude: number
    Pickup: {
      Total: number
      // Dynamic status counts - examples: Active, Completed, Pending, etc.
      [key: string]: number
    }
    Dropoff: {
      Total: number
      // Dynamic status counts - examples: Active, Completed, Pending, etc.
      [key: string]: number
    }
    TotalCount: number
  }
}

interface FeatureCollection {
  type: 'FeatureCollection'
  features: Feature[]
}

const nullIfEmpty = (value?: any[]) => {
  if (value?.length === 0) {
    return null
  }
  return value
}

export function useJobsGeoJSON() {
  const jobsFilterStore = useJobFilterStore()
  const legacyJobStore = useFilteredJobStoreLegacyAdapter()
  const lastModified = ref<Date | null>(null)
  const queryClient = useQueryClient()
  const isComponentMounted = ref(false)

  onMounted(() => {
    isComponentMounted.value = true
  })

  onBeforeUnmount(() => {
    isComponentMounted.value = false
  })

  const fetchJobsGeoJSON = async (
    isIncremental = false,
    signal?: AbortSignal
  ): Promise<FeatureCollection> => {
    const body = convertToSnakeCase({
      start: legacyJobStore.selectedStart.value,
      end: legacyJobStore.selectedEnd.value,
      divisions: nullIfEmpty(legacyJobStore.selectedDivisions.value),
      serviceProviders: nullIfEmpty(
        legacyJobStore.selectedServiceProviders.value
      ),
      statuses: nullIfEmpty(legacyJobStore.selectedJobStatuses.value),
      approvalStatuses: nullIfEmpty(
        legacyJobStore.selectedApprovalStatuses.value
      ),
      resourceTypes: nullIfEmpty(legacyJobStore.selectedResourceTypes.value),
      activities: nullIfEmpty(legacyJobStore.selectedActivities.value),
      priorities: nullIfEmpty(legacyJobStore.selectedPriorities.value),
      locations: nullIfEmpty(legacyJobStore.selectedLocations.value),
      batchStatuses: nullIfEmpty(jobsFilterStore.selectedBatchStatuses),
      modifiedSince: isIncremental
        ? moment(lastModified.value).subtract(10, 's').toISOString()
        : null,
    })

    const authService = getInstance()
    const token = await authService.getTokenSilently()

    const response = await fetch('/api/v3/field-view/geojson/jobs', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
      signal,
    })

    if (!response.ok) {
      throw new Error('Failed to fetch jobs GeoJSON')
    }

    const result = await response.json()

    // Update last modified timestamp
    if (!isIncremental) {
      lastModified.value = new Date()
    }

    return result
  }

  const mergeFeatureCollections = (
    existing: FeatureCollection,
    updates: FeatureCollection
  ): FeatureCollection => {
    if (!existing || !existing.features) {
      return updates
    }

    // Create a map of existing features by LocationId for efficient lookup
    const existingFeatureMap = new Map(
      existing.features.map((feature) => [
        feature.properties.LocationId,
        feature,
      ])
    )

    // Update or add features from the updates
    updates.features.forEach((updatedFeature) => {
      existingFeatureMap.set(
        updatedFeature.properties.LocationId,
        updatedFeature
      )
    })

    return {
      type: 'FeatureCollection',
      features: Array.from(existingFeatureMap.values()),
    }
  }

  // Create a computed query key that includes all filter dependencies
  const queryKey = computed(() => [
    'jobsGeoJSON',
    {
      start: legacyJobStore.selectedStart.value,
      end: legacyJobStore.selectedEnd.value,
      divisions: legacyJobStore.selectedDivisions.value,
      serviceProviders: legacyJobStore.selectedServiceProviders.value,
      statuses: legacyJobStore.selectedJobStatuses.value,
      approvalStatuses: legacyJobStore.selectedApprovalStatuses.value,
      resourceTypes: legacyJobStore.selectedResourceTypes.value,
      activities: legacyJobStore.selectedActivities.value,
      priorities: legacyJobStore.selectedPriorities.value,
      locations: legacyJobStore.selectedLocations.value,
      batchStatuses: jobsFilterStore.selectedBatchStatuses,
    },
  ])

  const {
    data: geoJsonData,
    refetch,
    isLoading,
    error,
    isRefetching,
  } = useQuery({
    queryKey,
    placeholderData: {
      type: 'FeatureCollection',
      features: [],
    } as FeatureCollection,
    queryFn: async (context) => {
      // Check if component is still mounted before proceeding
      if (!isComponentMounted.value) {
        throw new Error('Component unmounted')
      }

      const isIncremental = lastModified.value !== null
      const result = await fetchJobsGeoJSON(isIncremental, context.signal)

      // Check again after async operation
      if (!isComponentMounted.value) {
        throw new Error('Component unmounted during fetch')
      }

      if (isIncremental) {
        const previousData = queryClient.getQueryData<FeatureCollection>(
          context.queryKey
        )
        if (previousData) {
          const mergedData = mergeFeatureCollections(previousData, result)
          lastModified.value = new Date()
          return mergedData
        }
      }

      if (!isIncremental) {
        lastModified.value = new Date()
      }
      return result
    },
    keepPreviousData: true,
    refetchInterval: () => {
      // Only refetch if component is still mounted
      return isComponentMounted.value ? 30000 : false
    },
    staleTime: 0,
    enabled: isComponentMounted, // Only run query when component is mounted
  })

  // Function to reset incremental state and force full reload
  const resetAndRefetch = async () => {
    if (!isComponentMounted.value) {
      return Promise.resolve()
    }

    lastModified.value = null
    return refetch()
  }

  return {
    geoJsonData,
    refetch,
    resetAndRefetch,
    isLoading,
    error,
    isRefetching,
    lastModified,
    isComponentMounted,
  }
}

export type { Feature, FeatureCollection }
